import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  Box,
  Tooltip,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse,
  IconButton
} from '@mui/material';
import { Add as AddIcon, ExpandMore as ExpandMoreIcon, Info as InfoIcon, ExpandLess as ExpandLessIcon, ExpandMore as ExpandMoreIcon2 } from '@mui/icons-material';
import MessageTemplateService from '../../../services/messageTemplateService';
import EmailConfig from '../channels/EmailConfig';
import SMSConfig from '../channels/SMSConfig';
import VoiceConfig from '../channels/voice/VoiceConfig';
import ChatConfig from '../channels/ChatConfig';

const DripMessageConfig = ({ 
  messageSteps, 
  templates, 
  onMessageStepsChange,
  channel,
  channelConfig,
  onChannelConfigChange,
  communicationEndpoints,
  onEndpointChange,
  onChannelChange
}) => {


  const [variableCategories, setVariableCategories] = useState([]);
  const [showVariables, setShowVariables] = useState({});
  const [expandedVoiceConfigs, setExpandedVoiceConfigs] = useState({});

  useEffect(() => {
    const fetchAvailableVariables = async () => {
      try {
        const categories = await MessageTemplateService.getAvailableVariables();
        setVariableCategories(categories);
      } catch (err) {
        console.error('Failed to fetch available variables:', err);
      }
    };
    fetchAvailableVariables();
  }, []);

  // Update message steps when channel config changes
  useEffect(() => {
    if (channelConfig?.from_endpoint) {
      const updatedSteps = messageSteps.map(step => ({
        ...step,
        [`${channel}_config`]: {
          ...step[`${channel}_config`],
          from_endpoint: channelConfig.from_endpoint,
          from_endpoint_value: channelConfig.from_endpoint_value
        }
      }));
      onMessageStepsChange(updatedSteps);
    }
  }, [channelConfig?.from_endpoint]);

  const handleMessageStepChange = (index, field, value) => {
    const newSteps = [...messageSteps];
    if (field === 'content') {
      // Update content in the channel config
      newSteps[index] = {
        ...newSteps[index],
        [`${channel}_config`]: {
          ...newSteps[index][`${channel}_config`],
          content: value
        }
      };
    } else if (field === 'template') {
      // When template changes, update the step
      newSteps[index] = {
        ...newSteps[index],
        template: value,
        [`${channel}_config`]: {
          ...newSteps[index][`${channel}_config`],
          content: value ? '' : newSteps[index][`${channel}_config`]?.content || ''
        }
      };
    } else if (field.startsWith('voice_config.')) {
      // Handle voice configuration changes
      const voiceField = field.replace('voice_config.', '');
      newSteps[index] = {
        ...newSteps[index],
        voice_config: {
          ...newSteps[index].voice_config,
          [voiceField]: value
        }
      };
    } else if (field.startsWith('platform_config.')) {
      // Handle platform configuration changes
      const platformField = field.replace('platform_config.', '');
      newSteps[index] = {
        ...newSteps[index],
        voice_config: {
          ...newSteps[index].voice_config,
          platform_config: {
            ...newSteps[index].voice_config?.platform_config,
            [platformField]: value
          }
        }
      };
    } else if (field.startsWith('webhook_config.')) {
      // Handle webhook configuration changes
      const webhookField = field.replace('webhook_config.', '');
      newSteps[index] = {
        ...newSteps[index],
        voice_config: {
          ...newSteps[index].voice_config,
          webhook_config: {
            ...newSteps[index].voice_config?.webhook_config,
            [webhookField]: value
          }
        }
      };
    } else {
      newSteps[index] = {
        ...newSteps[index],
        [field]: value
      };
    }
    
    onMessageStepsChange(newSteps);
  };

  const addMessageStep = () => {
    // Get the from_endpoint from the channel config
    const fromEndpoint = channelConfig?.from_endpoint;
    const fromEndpointValue = channelConfig?.from_endpoint_value;
    
    // Create base step config
    let stepConfig = {
      content: '',
      from_endpoint: fromEndpoint,
      from_endpoint_value: fromEndpointValue,
      priority: 'normal',
      track_delivery: true,
      track_replies: true,
      media_urls: [],
      nurturing_campaign: messageSteps[0]?.[`${channel}_config`]?.nurturing_campaign
    };

    // Create voice config separately for voice channel
    let voiceConfig = null;
    if (channel === 'voice') {
      voiceConfig = {
        content: '',
        from_endpoint: fromEndpoint,
        from_endpoint_value: fromEndpointValue,
        platform: channelConfig?.platform || 'bland_ai',
        voice_id: channelConfig?.voice_id || 'alexa',
        voice_name: channelConfig?.voice_name || 'Alexa (Female)',
        temperature: channelConfig?.temperature || 0.7,
        max_duration: channelConfig?.max_duration || 5,
        retry_attempts: channelConfig?.retry_attempts || 3,
        voicemail_message: channelConfig?.voicemail_message || '',
        voicemail_action: channelConfig?.voicemail_action || 'hangup',
        record_call: channelConfig?.record_call ?? false,
        call_timeout: channelConfig?.call_timeout || 60,
        platform_config: {
          task: '',
          pathway_id: '',
          pathway_version: 1,
          first_sentence: '',
          background_track: '',
          model: 'base',
          wait_for_greeting: false,
          block_interruptions: false,
          interruption_threshold: 100,
          sensitive_voicemail_detection: false,
          transfer_phone_number: '',
          transfer_list: {},
          language: 'en-US',
          voicemail_sms: {},
          dispatch_hours: {},
          ignore_button_press: false,
          timezone: 'America/New_York',
          request_data: {},
          tools: [],
          start_time: '',
          voicemail_action: {},
          retry: { wait: 300, voicemail_action: 'leave_message' },
          max_duration: 5,
          record: false,
          webhook: '',
          webhook_events: [],
          metadata: {},
          citation_schema_id: '',
          analysis_preset: 'default',
          available_tags: [],
          geospatial_dialing: 'disabled',
          precall_dtmf_sequence: '',
          dynamic_data: [],
          keywords: [],
          pronunciation_guide: [],
          local_dialing: false,
          ...channelConfig?.platform_config
        },
        webhook_config: {
          webhook: '',
          webhook_events: [],
          ...channelConfig?.webhook_config
        },
        metadata: channelConfig?.metadata || {}
      };
    }
    
    const newStep = {
      order: messageSteps.length + 1,
      delay_units: 24,
      delay_unit_type: 'hours',
      template: '',
      [`${channel}_config`]: stepConfig,
      voice_config: voiceConfig
    };

    // Initialize other channel configs as null
    ['email', 'sms', 'voice', 'chat'].forEach(ch => {
      if (ch !== channel) {
        newStep[`${ch}_config`] = null;
      }
    });

    onMessageStepsChange([...messageSteps, newStep]);
  };

  const removeMessageStep = (index) => {
    const newSteps = messageSteps.filter((_, i) => i !== index);
    // Reorder remaining steps
    newSteps.forEach((step, i) => {
      step.order = i + 1;
    });
    onMessageStepsChange(newSteps);
  };

  const toggleVoiceConfig = (stepIndex) => {
    setExpandedVoiceConfigs(prev => ({
      ...prev,
      [stepIndex]: !prev[stepIndex]
    }));
  };

  const insertVariable = (stepIndex, categoryName, variableName) => {
    const variable = `{{${categoryName}.${variableName}}}`;
    const newSteps = [...messageSteps];
    newSteps[stepIndex] = {
      ...newSteps[stepIndex],
      [`${channel}_config`]: {
        ...newSteps[stepIndex][`${channel}_config`],
        content: (newSteps[stepIndex][`${channel}_config`]?.content || '') + variable
      }
    };
    onMessageStepsChange(newSteps);
  };

  const renderVariableList = (stepIndex) => {
    return variableCategories.map((category) => (
      <Accordion key={category.name}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`${category.name}-content`}
          id={`${category.name}-header`}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2">
              {category.name}
            </Typography>
            <Tooltip title={category.description}>
              <InfoIcon fontSize="small" color="action" />
            </Tooltip>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {category.variables.map((variable) => (
              <Tooltip 
                key={variable.name} 
                title={
                  <Box>
                    <Typography variant="body2">{variable.description}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Field: {variable.field_name}
                    </Typography>
                  </Box>
                }
              >
                <Chip
                  label={`{{${category.name}.${variable.name}}}`}
                  size="small"
                  onClick={() => insertVariable(stepIndex, category.name, variable.name)}
                  sx={{ 
                    '&:hover': {
                      backgroundColor: 'primary.light',
                      color: 'primary.contrastText'
                    }
                  }}
                />
              </Tooltip>
            ))}
          </Box>
        </AccordionDetails>
      </Accordion>
    ));
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>Channel</InputLabel>
          <Select
            value={channel}
            onChange={(e) => onChannelChange(e.target.value)}
            label="Channel"
          >
            <MenuItem value="email">Email</MenuItem>
            <MenuItem value="sms">SMS</MenuItem>
            <MenuItem value="voice">Voice</MenuItem>
            <MenuItem value="chat">Chat</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      {channel !== 'voice' && (
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            Channel Configuration
          </Typography>
          <Paper sx={{ p: 2, mb: 3 }}>
            {channel === 'email' && (
              <EmailConfig
                config={channelConfig}
                onConfigChange={onChannelConfigChange}
                communicationEndpoints={communicationEndpoints}
                onEndpointChange={onEndpointChange}
              />
            )}
            {channel === 'sms' && (
              <SMSConfig
                config={channelConfig}
                onConfigChange={onChannelConfigChange}
                communicationEndpoints={communicationEndpoints}
                onEndpointChange={onEndpointChange}
              />
            )}
            {channel === 'chat' && (
              <ChatConfig
                config={channelConfig}
                onConfigChange={onChannelConfigChange}
                communicationEndpoints={communicationEndpoints}
                onEndpointChange={onEndpointChange}
              />
            )}
          </Paper>
        </Grid>
      )}

      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1">
            Message Steps
          </Typography>
          {channel === 'voice' && messageSteps.length > 0 && (
            <Button
              size="small"
              variant="outlined"
              sx={{ 
                color: 'text.primary',
                borderColor: 'divider',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover'
                }
              }}
              onClick={() => {
                const allExpanded = messageSteps.every((_, i) => expandedVoiceConfigs[i]);
                const newExpandedState = {};
                messageSteps.forEach((_, i) => {
                  newExpandedState[i] = !allExpanded;
                });
                setExpandedVoiceConfigs(newExpandedState);
              }}
            >
              {messageSteps.every((_, i) => expandedVoiceConfigs[i]) ? 'Collapse All' : 'Expand All'}
            </Button>
          )}
        </Box>
        {messageSteps.map((step, index) => {
          return (
            <Paper key={index} sx={{ p: 2, mb: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12}>
                  <Typography variant="subtitle2">
                    Step {step.order}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    required
                    type="number"
                    label="Delay Units"
                    value={step.delay_units}
                    onChange={(e) => handleMessageStepChange(index, 'delay_units', parseInt(e.target.value) || 0)}
                    InputProps={{ inputProps: { min: 1 } }}
                    error={step.delay_units <= 0}
                    helperText={step.delay_units <= 0 ? "Delay units must be greater than 0" : ""}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth required>
                    <InputLabel>Delay Unit Type</InputLabel>
                    <Select
                      value={step.delay_unit_type}
                      onChange={(e) => handleMessageStepChange(index, 'delay_unit_type', e.target.value)}
                      label="Delay Unit Type"
                    >
                      <MenuItem value="minutes">Minutes</MenuItem>
                      <MenuItem value="hours">Hours</MenuItem>
                      <MenuItem value="days">Days</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  {channel !== 'voice' && (
                    <FormControl fullWidth>
                      <InputLabel>Template</InputLabel>
                      <Select
                        value={step.template}
                        onChange={(e) => handleMessageStepChange(index, 'template', e.target.value)}
                        label="Template"
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {templates.map((template) => (
                          <MenuItem key={template.id} value={template.id}>
                            {template.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  {channel === 'voice' && (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                      Voice message content is configured in the platform settings below
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => removeMessageStep(index)}
                    disabled={messageSteps.length === 1}
                  >
                    Remove
                  </Button>
                </Grid>
                {!step.template && channel !== 'voice' && (
                  <Grid item xs={12}>
                    <Box>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Message Content"
                        value={step[`${channel}_config`]?.content || ''}
                        onChange={(e) => handleMessageStepChange(index, 'content', e.target.value)}
                        error={!step.template && !step[`${channel}_config`]?.content}
                        helperText={!step.template && !step[`${channel}_config`]?.content ? "Either template or content is required" : ""}
                      />
                      <Box sx={{ mt: 2 }}>
                        <Button
                          variant="text"
                          onClick={() => setShowVariables(prev => ({ ...prev, [index]: !prev[index] }))}
                          startIcon={<ExpandMoreIcon sx={{ 
                            transform: showVariables[index] ? 'rotate(180deg)' : 'none',
                            transition: 'transform 0.3s'
                          }} />}
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            mb: showVariables[index] ? 2 : 0
                          }}
                        >
                          <Typography variant="subtitle2">
                            Add Message Variables
                          </Typography>
                          <Tooltip title="Click on a variable to insert it into your message">
                            <InfoIcon fontSize="small" />
                          </Tooltip>
                        </Button>
                        {showVariables[index] && (
                          <Box sx={{ mt: 1 }}>
                            {renderVariableList(index)}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                )}
                {channel === 'voice' && (
                  <Grid item xs={12}>
                    <Box sx={{ mt: 2 }}>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          cursor: 'pointer',
                          p: 1.5,
                          borderRadius: 1,
                          bgcolor: 'background.paper',
                          border: 1,
                          borderColor: 'divider',
                          '&:hover': { 
                            bgcolor: 'action.hover',
                            borderColor: 'primary.main'
                          },
                          transition: 'all 0.2s ease-in-out'
                        }}
                        onClick={() => toggleVoiceConfig(index)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                            Voice Configuration
                            {step.voice_config?.platform && (
                              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                ({step.voice_config.platform.toUpperCase()})
                              </Typography>
                            )}
                          </Typography>
                          {!expandedVoiceConfigs[index] && (
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              {step.voice_config ? (
                                <>
                                                                <Chip 
                                label={step.voice_config.platform?.toUpperCase() || 'BLAND AI'} 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                                sx={{ 
                                  color: 'primary.main',
                                  borderColor: 'primary.main',
                                  '&:hover': {
                                    bgcolor: 'primary.main',
                                    color: 'primary.contrastText'
                                  }
                                }}
                              />
                                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {step.voice_config.voice_name || step.voice_config.voice_id || 'Default Voice'}
                              </Typography>
                                                                {step.voice_config.platform === 'bland_ai' && step.voice_config.platform_config?.pathway_id && (
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  • Pathway: {step.voice_config.platform_config.pathway_id}
                                </Typography>
                              )}
                                </>
                              ) : (
                                <Chip 
                                  label="Not Configured" 
                                  size="small" 
                                  color="warning" 
                                  variant="outlined"
                                  sx={{ 
                                    color: 'warning.main',
                                    borderColor: 'warning.main',
                                    '&:hover': {
                                      bgcolor: 'warning.main',
                                      color: 'warning.contrastText'
                                    }
                                  }}
                                />
                              )}
                            </Box>
                          )}
                        </Box>
                        <IconButton size="small" sx={{ color: 'text.primary' }}>
                          {expandedVoiceConfigs[index] ? <ExpandLessIcon /> : <ExpandMoreIcon2 />}
                        </IconButton>
                      </Box>
                      <Collapse in={expandedVoiceConfigs[index]}>
                        <Paper sx={{ 
                          p: 2, 
                          bgcolor: 'background.paper', 
                          mt: 1, 
                          border: 1, 
                          borderColor: 'divider',
                          boxShadow: 1
                        }}>
                          <VoiceConfig
                            config={step.voice_config || {}}
                            onConfigChange={(config) => {
                              const newSteps = [...messageSteps];
                              newSteps[index] = {
                                ...newSteps[index],
                                voice_config: config
                              };
                              onMessageStepsChange(newSteps);
                            }}
                            communicationEndpoints={communicationEndpoints}
                            onEndpointChange={(value) => {
                              const newSteps = [...messageSteps];
                              newSteps[index] = {
                                ...newSteps[index],
                                voice_config: {
                                  ...newSteps[index].voice_config,
                                  from_endpoint: value
                                }
                              };
                              onMessageStepsChange(newSteps);
                            }}
                          />
                        </Paper>
                      </Collapse>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Paper>
          );
        })}
        <Button
          variant="outlined"
          onClick={addMessageStep}
          startIcon={<AddIcon />}
        >
          Add Message Step
        </Button>
      </Grid>
    </Grid>
  );
};

export default DripMessageConfig; 